import {Matrix4, Vector3} from "@cm/math"
import type {RawPolyMeshData} from "#template/geometry-processing/raw-poly-mesh-data"
import {from3dsCoordinate, from3dsMaxTransform, to3dsCoordinate} from "#template/utils/3ds-max-utils"

// Blender works in metres with Z up, the platform in centimetres with Y up
const centimetresPerMetre = 100

type Vector3Like = {x: number; y: number; z: number}

export function fromBlenderLength(metres: number): number {
    return metres * centimetresPerMetre
}

export function toBlenderLength(centimetres: number): number {
    return centimetres / centimetresPerMetre
}

export function fromBlenderDirection({x, y, z}: Vector3Like): Vector3 {
    return from3dsCoordinate(new Vector3(x, y, z))
}

export function toBlenderDirection({x, y, z}: Vector3Like): Vector3 {
    return to3dsCoordinate(new Vector3(x, y, z))
}

export function fromBlenderCoordinate(position: Vector3Like): Vector3 {
    return fromBlenderDirection(position).mul(fromBlenderLength(1))
}

export function toBlenderCoordinate(position: Vector3Like): Vector3 {
    return toBlenderDirection(position).mul(toBlenderLength(1))
}

// with orientation, the object keeps Blender's local axes, as cameras, lights and curves do; without, its local axes are converted like the world's
export function fromBlenderTransform(location: Vector3Like, rotation: Vector3Like, orientation: boolean): Matrix4 {
    const translation = new Vector3(fromBlenderLength(location.x), fromBlenderLength(location.y), fromBlenderLength(location.z))
    return from3dsMaxTransform(translation, new Vector3(rotation.x, rotation.y, rotation.z), undefined, orientation)
}

export const platformToBlenderAxes = Matrix4.fromArray([1, 0, 0, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 0, 0, 1])
export const blenderToPlatformAxes = platformToBlenderAxes.inverse()

// Blender's matrix_world, row by row
export function toBlenderTransform(transform: Matrix4, orientation: boolean): number[] {
    const inBlenderAxes = platformToBlenderAxes.multiply(transform)
    const rows = (orientation ? inBlenderAxes : inBlenderAxes.multiply(blenderToPlatformAxes)).toArrayTransposed()
    for (const translation of [3, 7, 11]) rows[translation] = toBlenderLength(rows[translation])
    return rows
}

function convertMesh(mesh: RawPolyMeshData, convertPosition: (v: Vector3Like) => Vector3, convertDirection: (v: Vector3Like) => Vector3): RawPolyMeshData {
    const convert = (values: Float32Array, convertVector: (v: Vector3Like) => Vector3) => {
        const converted = new Float32Array(values.length)
        for (let i = 0; i < values.length; i += 3) {
            const {x, y, z} = convertVector({x: values[i], y: values[i + 1], z: values[i + 2]})
            converted[i] = x
            converted[i + 1] = y
            converted[i + 2] = z
        }
        return converted
    }
    return {
        ...mesh,
        position: {...mesh.position, values: convert(mesh.position.values, convertPosition)},
        normal: {...mesh.normal, values: convert(mesh.normal.values, convertDirection)},
    }
}

export function fromBlenderCoordinates(mesh: RawPolyMeshData): RawPolyMeshData {
    return convertMesh(mesh, fromBlenderCoordinate, fromBlenderDirection)
}

export function toBlenderCoordinates(mesh: RawPolyMeshData): RawPolyMeshData {
    return convertMesh(mesh, toBlenderCoordinate, toBlenderDirection)
}
